import * as React from 'react';
import { useSelector } from 'react-redux';
import { isNil } from 'lodash';

import ConsentPlaceholder from 'components/consent-placeholder/ConsentPlaceholder';

import { getCloudinaryImageUrl, getCloudinaryVideoThumbnail } from 'utils/cloudinary';
import {
  isInstagramUrl,
  isS3Url,
  isTikTokVideoUrl,
  isTiktokCDNUrl,
  isValidAssetUrl,
  isYoutubeCDNUrl,
  isYoutubeImageCDNUrl,
  isYoutubeUrl
} from 'utils/url';
import { DEFAULT_PLACEHOLDER_IMAGE, LOADING_PLACEHOLDER_IMAGE } from 'utils/image';
import { getTikTokThumbnailUrlFromVideoUrl } from 'utils/tiktok-thumbnail';
import { CHANNEL_TYPE_TIKTOK } from 'utils/channels';

import type { GalleryModalItem, State } from 'types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

type Props = {
  item: GalleryModalItem,
  index: number,
  thumbClasses: string,
  renderCustomThumbnailOverlay?: (item: GalleryModalItem) => React.ReactNode,
  onThumbnailClick: (event: React.MouseEvent) => void,
  userChannelConsents: ConsentReducerConsentsMap
};

import './gallery-modal-thumbnail.scss';

function getAssetType(item: GalleryModalItem): 'instagram' | 'tiktok' | 'youtube' | undefined {
  if (item.type === 'instagram' || isInstagramUrl(item.url)) {
    return 'instagram';
  }

  if (
    item.type === 'youtube'
    || isYoutubeUrl(item.url)
    || isYoutubeImageCDNUrl(item.assetUrl)
    || isYoutubeCDNUrl(item.assetUrl)
  ) {
    return 'youtube';
  }

  if (
    item.type === 'tiktok'
    || isTikTokVideoUrl(item.url)
    || isTiktokCDNUrl(item.assetUrl ?? '')
  ) {
    return 'tiktok';
  }

  return undefined;
}

const GalleryModalThumbnail = (props: Props) => {
  const {
    item,
    index,
    thumbClasses,
    renderCustomThumbnailOverlay,
    onThumbnailClick,
    userChannelConsents
  } = props;
  const [assetUrl, setAssetUrl] = React.useState(item.assetUrl);
  const videoMapping = useSelector((state: State) => state.cloudinaryVideos.videoMapping);

  React.useEffect(() => {
    (async () => {
      if (item.type === 'cloudinary-video') {
        setAssetUrl(getCloudinaryVideoThumbnail(item.assetUrl));
      } else if (item.type === 'cloudinary-image' || item.type === 'cloudinary-pdf') {
        setAssetUrl(getCloudinaryImageUrl({ url: item.assetUrl }, { width: 80, height: 80 }));
      } else if (userChannelConsents[CHANNEL_TYPE_TIKTOK] && item.type === 'tiktok' && !isTiktokCDNUrl(item.assetUrl || '')) {
        try {
          const imageUrl = item.url ? await getTikTokThumbnailUrlFromVideoUrl(item.url) : '';

          setAssetUrl(imageUrl);
        } catch (e) {
          setAssetUrl(item.assetUrl);
        }
      } else if (item.useAssetDirectly) {
        setAssetUrl(item.thumbnailUrl || item.assetUrl);
      }
    })();
  }, [item.assetUrl]);

  const assetType = getAssetType(item);
  const hasConsent = assetType === undefined || userChannelConsents[assetType];

  let mappedAssetUrl: string | undefined = assetUrl;

  if (item.type === 'collabary-video') {
    if (!isNil(assetUrl) && videoMapping[assetUrl]) {
      mappedAssetUrl = getCloudinaryVideoThumbnail(videoMapping[assetUrl].cloudinaryUrl);
    } else if (item.useAssetDirectly) {
      mappedAssetUrl = getCloudinaryImageUrl({ url: item.thumbnailUrl ?? '' }, { width: 80, height: 80 });
    } else if (isS3Url(item.url ?? '')) {
      mappedAssetUrl = item.assetUrl ?? '';
    } else if (isNil(item.url) && !isNil(item.assetUrl)) {
      mappedAssetUrl = item.assetUrl;
    } else {
      mappedAssetUrl = LOADING_PLACEHOLDER_IMAGE;
    }
  }

  const isValidAssetURL = isValidAssetUrl(mappedAssetUrl) || mappedAssetUrl === LOADING_PLACEHOLDER_IMAGE;

  return (
    <li
      className="gallery-modal__thumb"
      data-position={index}
      onClick={onThumbnailClick}
    >
      {hasConsent && (
        <div
          className={thumbClasses}
          style={{ backgroundImage: isValidAssetURL ? `url(${mappedAssetUrl})` : `url(${DEFAULT_PLACEHOLDER_IMAGE})` }}
        />
      )}
      {!hasConsent && assetType && (
        <div className={thumbClasses}>
          <ConsentPlaceholder
            type={assetType}
          />
        </div>
      )}
      {
        renderCustomThumbnailOverlay && renderCustomThumbnailOverlay(item)
      }
    </li>
  );
};

export default GalleryModalThumbnail;
